import { useSupportEmail } from '@/contexts/SupportEmail/SupportEmailContext';
import {
  CompanyReasonableAdjustmentsOptionFragment,
  ReasonableAdjustmentsOptionKind,
} from 'generated/graphql';
import { ChangeEvent, FormEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Link, RadioButton } from '@spotted-zebra-uk/ui-components';
import {
  steps,
  TAdjustmentsFlowRoutes,
} from '../AccessibilityAdjustments.helpers';
import styles from './AccessibilityIntro.module.scss';

const learnMoreLink =
  'https://communications.spottedzebra.co.uk/knowledge-base/can-you-make-reasonable-adjustments';

const AccessibilityIntro = ({
  companyName,
  flowRoutes,
  onContinue,
  onCancelFlow,
  loading,
  companyReasonableAdjustmentsOption,
}: {
  companyName: string;
  flowRoutes: TAdjustmentsFlowRoutes;
  onContinue: (step: steps) => void;
  onCancelFlow: (redirectUrl: string) => void;
  loading: boolean;
  companyReasonableAdjustmentsOption?: CompanyReasonableAdjustmentsOptionFragment;
}) => {
  const { t } = useTranslation();

  return (
    <section className={styles.container} data-testid="accessibility-intro">
      <p className={styles.description}>
        {t('accessibilityAdjustments.intro.description')}
      </p>
      <Link to={learnMoreLink} target="_blank">
        {t('accessibilityAdjustments.intro.learnAboutLink')}
      </Link>
      {companyReasonableAdjustmentsOption?.kind ===
        ReasonableAdjustmentsOptionKind.SelfServe && (
        <AccessibilityIntroQuestionnaire
          companyName={companyName}
          flowRoutes={flowRoutes}
          loading={loading}
          onContinue={onContinue}
          onCancelFlow={onCancelFlow}
        />
      )}
      {companyReasonableAdjustmentsOption?.kind &&
        [
          ReasonableAdjustmentsOptionKind.ContactCompanyEmail,
          ReasonableAdjustmentsOptionKind.ContactCompanyUrl,
          ReasonableAdjustmentsOptionKind.EmailSz,
          ReasonableAdjustmentsOptionKind.ContactCompanyGeneric,
        ].includes(companyReasonableAdjustmentsOption.kind) && (
          <AccessibilityIntroManualContact
            companyName={companyName}
            companyReasonableAdjustmentsOption={
              companyReasonableAdjustmentsOption
            }
            loading={loading}
            flowRoutes={flowRoutes}
            onContinue={onCancelFlow}
          />
        )}
    </section>
  );
};

type AccessibilityIntroManualContactProps = {
  companyName: string;
  companyReasonableAdjustmentsOption: CompanyReasonableAdjustmentsOptionFragment;
  flowRoutes: TAdjustmentsFlowRoutes;
  loading: boolean;
  onContinue: (redirectUrl: string) => void;
};

function AccessibilityIntroManualContact({
  companyName,
  companyReasonableAdjustmentsOption,
  loading,
  flowRoutes,
  onContinue,
}: AccessibilityIntroManualContactProps) {
  const { t } = useTranslation();
  const { supportEmail, supportEmailHref } = useSupportEmail();

  const handleContinue = () => {
    onContinue(flowRoutes.exitUrl);
  };

  const getGetContactReferenceData = () => {
    switch (companyReasonableAdjustmentsOption.kind) {
      case ReasonableAdjustmentsOptionKind.ContactCompanyEmail:
        return {
          link: `mailto:${companyReasonableAdjustmentsOption.value}`,
          linkText: companyName,
          ariaLabel: t('common.sendEmailTo', {
            email: companyReasonableAdjustmentsOption?.value,
          }),
        };
      case ReasonableAdjustmentsOptionKind.ContactCompanyUrl:
        return {
          link: companyReasonableAdjustmentsOption.value as string,
          linkText: companyName,
          ariaLabel: t('accessibilityAdjustments.intro.openCompanyPage', {
            companyName,
          }),
        };
      default:
        return {
          link: supportEmailHref,
          linkText: supportEmail,
          ariaLabel: t('common.sendEmailTo', {
            email: supportEmail,
          }),
        };
    }
  };

  const renderContactReference = () => {
    if (
      companyReasonableAdjustmentsOption.kind ===
      ReasonableAdjustmentsOptionKind.ContactCompanyGeneric
    ) {
      return (
        <div className={styles.text}>
          <Trans
            i18nKey="accessibilityAdjustments.intro.contactCompanyString"
            values={{ companyName }}
          >
            Please contact
            <span className={styles.strong}>{companyName}</span>
            to request any reasonable adjustments.
          </Trans>
        </div>
      );
    }

    const { linkText, link, ariaLabel } = getGetContactReferenceData();

    return (
      <div className={styles.text}>
        <Trans
          i18nKey="accessibilityAdjustments.intro.contactCompanyLink"
          values={{ linkText }}
        >
          Please contact
          <Link
            target="_blank"
            to={link}
            aria-label={ariaLabel}
            className={styles.link}
            data-testid="accessibility-intro__manual-contact-link"
          >
            {linkText}
          </Link>
          to request any reasonable adjustments.
        </Trans>
      </div>
    );
  };

  return (
    <div
      className={styles.contactCompanyEmailSection}
      data-testid="accessibility-intro__manual-contact"
    >
      {renderContactReference()}
      <Button
        onClick={handleContinue}
        className={styles.cta}
        type="submit"
        loading={loading}
        data-testid="accessibility-intro__manual-contact-continue-button"
      >
        {t(`common.continue`)}
      </Button>
    </div>
  );
}

type AccessibilityIntroQuestionnaireProps = {
  companyName: string;
  flowRoutes: TAdjustmentsFlowRoutes;
  loading: boolean;
  onContinue: (step: steps) => void;
  onCancelFlow: (redirectUrl: string) => void;
};

function AccessibilityIntroQuestionnaire({
  companyName,
  flowRoutes,
  loading,
  onContinue,
  onCancelFlow,
}: AccessibilityIntroQuestionnaireProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requiresAdjustments, setRequiresAdjustments] = useState<string>('');
  const [requiresAdjustmentsError, setRequiresAdjustmentsError] =
    useState<string>('');
  const options = [
    { id: 'yes', label: t('accessibilityAdjustments.intro.yes'), value: 'yes' },
    { id: 'no', label: t('accessibilityAdjustments.intro.no'), value: 'no' },
  ];

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    _id: string | undefined
  ) => {
    if (requiresAdjustmentsError) {
      setRequiresAdjustmentsError('');
    }
    setRequiresAdjustments(e.target.value || '');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Throw error if no option selected before clicking Continue
    if (!requiresAdjustments) {
      setRequiresAdjustmentsError(t('accessibilityAdjustments.intro.error'));
      return;
    }
    // Close Questionnaire flow if "No" answer is selected. Navigation happens
    // when the submit mutation completes — navigating immediately would race
    // the mutation and trip the exit page's adjustments gate.
    if (requiresAdjustments === 'no') {
      onCancelFlow(flowRoutes.exitUrl);
      onContinue('intro');
      return;
    }
    onContinue('intro');
    navigate(flowRoutes.getStepUrl('options'));
  };

  return (
    <form
      className={styles.formContainer}
      onSubmit={handleSubmit}
      data-testid="accessibility-intro__questionnaire"
    >
      <h2 className={styles.title}>
        {t('accessibilityAdjustments.intro.formQuestion')}
      </h2>
      <p className={styles.formDescription}>
        {t('accessibilityAdjustments.intro.formDescription', {
          companyName: companyName,
        })}
      </p>
      {options.map((item, index) => (
        <div
          data-testid={`accessibility-intro__checkbox-${item.value}`}
          key={index}
        >
          <RadioButton
            name={`requiresAdjustments-${item.label}`}
            id={item.id}
            onChange={handleChange}
            value={item.value}
            checked={requiresAdjustments === item.value}
            className={styles.checkbox}
            hasError={Boolean(requiresAdjustmentsError)}
          >
            {item.label}
          </RadioButton>
        </div>
      ))}
      {requiresAdjustmentsError && (
        <p
          className={styles.error}
          data-testid="accessibility-intro__submit-error"
        >
          {requiresAdjustmentsError}
        </p>
      )}
      <Button
        onClick={handleSubmit}
        className={styles.cta}
        type="submit"
        data-testid="accessibility-intro__submit-button"
        loading={loading}
      >
        {t(`common.continue`)}
      </Button>
    </form>
  );
}

export default AccessibilityIntro;
